<template>
  <el-dialog :title="title" :visible.sync="show" width="720px" top="6vh"
             :close-on-click-modal="false" append-to-body @closed="onClosed">
    <div v-if="mode === 'scan'">
      <div class="gpy-tip">
        <el-tag size="small" :type="connected ? 'success' : (failed ? 'danger' : 'info')">
          {{ connected ? '高拍仪已连接' : (failed ? '未检测到高拍仪服务' : '正在连接高拍仪…') }}
        </el-tag>
        <span class="gpy-pages" v-if="connected">已扫页数：{{ pageCount }}</span>
      </div>
      <!-- 预览画布：仅"真高拍仪已连接"时显示；连接中/失败/用本地摄像头都隐藏（但元素保留以便 SDK 初始化画布） -->
      <div v-show="connected" id="gpyPreviewBox" ref="previewBox" class="gpy-preview"></div>
      <div v-if="!connected && !failed" class="gpy-connecting">正在连接高拍仪，请稍候…</div>
      <div class="gpy-actions" v-if="connected">
        <el-button size="small" icon="el-icon-camera" @click="addPage" :loading="busy">扫描一页</el-button>
        <el-button size="small" @click="resetPages" :disabled="pageCount === 0 || busy">重扫</el-button>
        <el-button size="small" type="primary" icon="el-icon-document"
                   :disabled="pageCount === 0 || busy" :loading="busy" @click="combineAndSave">
          生成 PDF 并保存（{{ pageCount }} 页）
        </el-button>
      </div>
      <div class="gpy-alt" v-if="connected">
        <el-button size="mini" type="text" icon="el-icon-camera" @click="openCamera">没有高拍仪？用电脑摄像头拍照</el-button>
      </div>
      <div class="gpy-fallback" v-if="failed">
        <p class="muted small">未检测到本机高拍仪服务（KMGpyServer 未安装或未启动）。可改为用电脑摄像头拍照，或手动上传已扫描好的文件（PDF / Word / 图片）。</p>
        <div class="gpy-fb-btns">
          <el-button size="small" type="primary" icon="el-icon-camera" @click="openCamera">用摄像头拍照</el-button>
          <el-upload class="gpy-fb-upload" action="#" :auto-upload="false" :show-file-list="false"
                     accept=".pdf,.doc,.docx,image/*"
                     :on-change="onManualPick">
            <el-button size="small" type="warning" icon="el-icon-upload2">手动上传文件</el-button>
          </el-upload>
          <el-button size="small" @click="retry">重试连接</el-button>
        </div>
      </div>
    </div>

    <div v-else-if="mode === 'camera'">
      <div class="gpy-tip">
        <el-tag size="small" :type="cameraError ? 'danger' : 'success'">
          {{ cameraError ? '摄像头不可用' : '电脑摄像头' }}
        </el-tag>
        <span v-if="!cameraError" class="gpy-pages">已拍页数：{{ photos.length }}</span>
      </div>
      <video v-show="!cameraError" ref="video" class="gpy-cam" autoplay playsinline muted></video>
      <div v-if="cameraError" class="gpy-camerr">{{ cameraError }}</div>
      <div class="gpy-shots" v-if="photos.length">
        <div v-for="(p, i) in photos" :key="i" class="gpy-shot">
          <img :src="p" />
          <i class="el-icon-close" @click="removePhoto(i)"></i>
        </div>
      </div>
      <div class="gpy-actions">
        <el-button size="small" icon="el-icon-camera" :disabled="!!cameraError || busy" @click="capturePhoto">拍这一页</el-button>
        <el-button size="small" :disabled="photos.length === 0 || busy" @click="photos = []">清空</el-button>
        <el-button size="small" type="primary" icon="el-icon-upload2"
                   :disabled="photos.length === 0 || busy" :loading="busy" @click="uploadPhotos">
          上传（{{ photos.length }} 张）
        </el-button>
        <el-button size="small" @click="backToScan">返回</el-button>
      </div>
      <canvas ref="canvas" style="display:none"></canvas>
    </div>

    <div slot="footer">
      <el-button size="small" @click="show = false">关闭</el-button>
    </div>
  </el-dialog>
</template>

<script>
import axios from 'axios'

// gpyhs.js 是全局函数 + 全局回调风格；用一个模块级 current 把回调路由到当前打开的实例
let scriptLoading = null
let activeInst = null

function loadGpy() {
  if (window.Cam_ControlInit) return Promise.resolve()
  if (scriptLoading) return scriptLoading
  scriptLoading = new Promise((resolve, reject) => {
    const s = document.createElement('script')
    s.src = '/gpyhs.js'
    s.onload = () => resolve()
    s.onerror = () => reject(new Error('gpyhs.js 加载失败'))
    document.head.appendChild(s)
  })
  return scriptLoading
}

// 必须在 window 上提供 gpyhs.js 会回调的这些函数（只挂一次，转发给 activeInst）
function installCallbacks() {
  if (window.__gpyCbInstalled) return
  window.__gpyCbInstalled = true
  const fwd = (name) => function () {
    if (activeInst && typeof activeInst.gpyCb === 'function') {
      activeInst.gpyCb(name, Array.prototype.slice.call(arguments))
    }
  }
  window.GetDevCountAndNameResultCB = fwd('devCount')
  window.GetResolutionResultCB = fwd('resolution')
  window.GetCameraOnOffStatus = fwd('camStatus')
  window.GetCaptrueImgResultCB = fwd('capture')
  window.AddImgFileToPDFResultCB = fwd('addPdf')
  window.PdfCombineResultCB = fwd('combinePdf')
}

export default {
  name: 'GaopaiyiScanner',
  props: {
    visible: { type: Boolean, default: false },
    title: { type: String, default: '高拍仪扫描签约文件' },
    bizType: { type: String, default: 'agreement' }
  },
  data() {
    return {
      show: this.visible,
      mode: 'scan',
      connected: false,
      failed: false,
      busy: false,
      pageCount: 0,
      connectTimer: null,
      cameraStream: null,
      cameraError: '',
      photos: []
    }
  },
  watch: {
    visible(v) { this.show = v; if (v) this.start() },
    show(v) { this.$emit('update:visible', v) }
  },
  methods: {
    async start() {
      this.mode = 'scan'; this.connected = false; this.failed = false
      this.busy = false; this.pageCount = 0
      activeInst = this
      try {
        await loadGpy()
        installCallbacks()
        this.$nextTick(() => {
          const box = this.$refs.previewBox
          // 清掉上一次 SDK 注入的旧 MainCamCanvas，避免反复 Cam_ControlInit 叠加多个画布
          if (box) box.innerHTML = ''
          // 用固定尺寸（预览框此时 v-show=false，clientWidth 为 0 会导致 SDK 不建画布）
          window.Cam_ControlInit(box, 0, 0, 680, 420)
          // 连接探测：3.5s 内没回设备数则判定服务不可用
          clearTimeout(this.connectTimer)
          this.connectTimer = setTimeout(() => {
            if (!this.connected) this.failed = true
          }, 3500)
        })
      } catch (e) {
        this.failed = true
      }
    },
    // ---- gpyhs.js 回调统一分发 ----
    gpyCb(name, args) {
      if (name === 'devCount') {
        const devCount = args[0]
        if (devCount > 0) {
          try { window.Cam_GetDevResolution(0) } catch (e) { this.openCam(1600, 1200) }
        } else { this.failed = true }
      } else if (name === 'resolution') {
        let bw = 1600, bh = 1200
        try {
          (args[1] || []).forEach(r => {
            const p = String(r).split('*')
            const w = Number(p[0]) || 0, h = Number(p[1]) || 0
            if (w * h > bw * bh) { bw = w; bh = h }
          })
        } catch (e) { /* ignore */ }
        this.openCam(bw, bh)
      } else if (name === 'camStatus') {
        if (args[0] === 0) { this.connected = true; this.failed = false; clearTimeout(this.connectTimer) }
      } else if (name === 'addPdf') {
        this.busy = false
        if (args[0] === 0) { this.pageCount++ } else { this.$message.error('扫描失败，请重试') }
      } else if (name === 'combinePdf') {
        const flag = args[0]; const base64Str = args[1]
        if (flag !== 0 || !base64Str) { this.busy = false; this.$message.error('生成 PDF 失败'); return }
        this.uploadPdf(base64Str)
      }
    },
    openCam(w, h) {
      try {
        window.Cam_Open(0, w, h)
        window.Cam_SetCutMode && window.Cam_SetCutMode(1) // 自动裁切
        window.Cam_SetFileType && window.Cam_SetFileType(3) // pdf
      } catch (e) { this.failed = true }
    },
    // ---- 操作 ----
    addPage() {
      if (this.busy) return
      this.busy = true
      try { window.Cam_AddImgFileToPDF('') } catch (e) { this.busy = false; this.$message.error('调用失败') }
    },
    resetPages() {
      // 重新初始化即清空已加入的页
      this.pageCount = 0
      this.start()
    },
    combineAndSave() {
      if (this.pageCount === 0 || this.busy) return
      this.busy = true
      try { window.Cam_CombinePDF('') } catch (e) { this.busy = false; this.$message.error('合成失败') }
    },
    uploadPdf(base64Str) {
      const data = base64Str.indexOf(',') >= 0 ? base64Str : ('data:application/pdf;base64,' + base64Str)
      axios.post('/file/upload/base64', { data, ext: 'pdf', type: this.bizType })
        .then(res => {
          this.busy = false
          if (res.data && res.data.success && res.data.url) {
            this.$message.success('签约文件已保存')
            this.$emit('uploaded', res.data.url)
            this.show = false
          } else {
            this.$message.error((res.data && res.data.message) || '上传失败')
          }
        })
        .catch(() => { this.busy = false; this.$message.error('上传失败') })
    },
    onManualPick(file) {
      if (!file || !file.raw) return
      const fd = new FormData()
      fd.append('file', file.raw)
      axios.post('/file/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
        .then(res => {
          if (res.data && res.data.url) {
            this.$message.success('已上传')
            this.$emit('uploaded', res.data.url)
            this.show = false
          } else {
            this.$message.error('上传失败')
          }
        })
        .catch(() => this.$message.error('上传失败'))
    },
    retry() { this.start() },
    // ---- B 路径：浏览器调用本机摄像头拍照 ----
    async openCamera() {
      this.mode = 'camera'
      this.cameraError = ''
      this.photos = []
      try { window.Cam_Close && window.Cam_Close() } catch (e) { /* ignore */ }
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.cameraError = '当前浏览器不支持摄像头（需 HTTPS 或 localhost）'
        return
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } },
          audio: false
        })
        this.cameraStream = stream
        this.$nextTick(() => {
          const v = this.$refs.video
          if (v) { v.srcObject = stream; v.play && v.play() }
        })
      } catch (e) {
        this.cameraError = '无法访问摄像头：' + (e && e.message ? e.message : '权限被拒绝')
      }
    },
    capturePhoto() {
      const v = this.$refs.video
      const c = this.$refs.canvas
      if (!v || !c || !v.videoWidth) { this.$message.error('摄像头未就绪'); return }
      c.width = v.videoWidth
      c.height = v.videoHeight
      c.getContext('2d').drawImage(v, 0, 0, c.width, c.height)
      this.photos.push(c.toDataURL('image/jpeg', 0.85))
    },
    removePhoto(i) { this.photos.splice(i, 1) },
    async uploadPhotos() {
      if (this.photos.length === 0 || this.busy) return
      this.busy = true
      try {
        const urls = []
        for (const data of this.photos) {
          const res = await axios.post('/file/upload/base64', { data, ext: 'jpg', type: this.bizType })
          if (res.data && res.data.success && res.data.url) urls.push(res.data.url)
          else throw new Error((res.data && res.data.message) || '上传失败')
        }
        urls.forEach(u => this.$emit('uploaded', u))
        this.$message.success('已上传 ' + urls.length + ' 张签约文件')
        this.show = false
      } catch (e) {
        this.$message.error(e && e.message ? e.message : '上传失败')
      } finally {
        this.busy = false
      }
    },
    stopCamera() {
      if (this.cameraStream) {
        try { this.cameraStream.getTracks().forEach(t => t.stop()) } catch (e) { /* ignore */ }
        this.cameraStream = null
      }
    },
    backToScan() {
      this.stopCamera()
      this.start()
    },
    onClosed() {
      clearTimeout(this.connectTimer)
      this.stopCamera()
      try { window.Cam_Close && window.Cam_Close() } catch (e) { /* ignore */ }
      if (activeInst === this) activeInst = null
    }
  }
}
</script>

<style scoped>
.gpy-tip { display: flex; align-items: center; gap: 12px; margin-bottom: 10px; }
.gpy-pages { color: #606266; font-size: 13px; }
.gpy-preview { width: 100%; height: 420px; background: #1f2d3d; border-radius: 4px; overflow: hidden; }
.gpy-actions { margin-top: 12px; display: flex; gap: 10px; flex-wrap: wrap; align-items: center; }
.gpy-fallback { margin-top: 14px; }
.gpy-fallback .muted { color: #909399; margin: 0 0 10px; }
.gpy-fallback .small { font-size: 12px; }
.gpy-fb-btns { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.gpy-fb-btns .gpy-fb-upload { display: inline-flex; }
.gpy-fb-btns >>> .el-upload { display: inline-flex; }
.gpy-alt { margin-top: 10px; }
.gpy-cam {
  display: block;
  width: 100%;
  max-width: 100%;
  height: 380px;
  object-fit: contain;
  background: #1f2d3d;
  border-radius: 4px;
  margin: 0;
}
.gpy-camerr { width: 100%; padding: 24px; text-align: center; color: #f56c6c; background: #fef0f0; border: 1px solid #fde2e2; border-radius: 4px; box-sizing: border-box; }
.gpy-connecting { width: 100%; padding: 28px; text-align: center; color: #909399; background: #f4f4f5; border-radius: 4px; box-sizing: border-box; }
.gpy-shots { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 10px; }
.gpy-shot { position: relative; width: 84px; height: 84px; border: 1px solid #dcdfe6; border-radius: 4px; overflow: hidden; }
.gpy-shot img { width: 100%; height: 100%; object-fit: cover; }
.gpy-shot .el-icon-close { position: absolute; top: 2px; right: 2px; background: rgba(0,0,0,.5); color: #fff; border-radius: 50%; cursor: pointer; font-size: 14px; }
.muted.small { color: #909399; font-size: 12px; }
</style>
